"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { BottomNav } from "@/components/bottom-nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  Users,
  Briefcase,
  Handshake,
  Star,
  TrendingUp,
  HardHat,
  Building2,
  Loader2,
  RefreshCw,
} from "lucide-react";

interface Analytics {
  totals: {
    workers: number;
    contractors: number;
    jobs: number;
    activeJobs: number;
    matches: number;
    hires: number;
    ratings: number;
  };
  funnel: {
    signups7d: { workers: number; contractors: number; total: number };
    hireRate: number;
  };
}

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{ backgroundColor: color + "15" }}
          >
            {icon}
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const router = useRouter();
  const supabase = createClient();
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadAnalytics() {
    setLoading(true);
    try {
      const res = await fetch("/api/analytics");
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Check auth
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) router.push("/auth/login");
      else loadAnalytics();
    });
  }, [supabase, router]);

  if (loading && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="mx-auto max-w-screen-sm px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => router.back()}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold">Platform Analytics</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={loadAnalytics}
            disabled={loading}
          >
            <RefreshCw
              className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
            />
          </Button>
        </div>

        {data && (
          <div className="space-y-6">
            {/* Key metrics */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Platform Totals
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  icon={<HardHat className="h-5 w-5" style={{ color: "#E06B20" }} />}
                  label="Workers"
                  value={data.totals.workers}
                  color="#E06B20"
                />
                <StatCard
                  icon={<Building2 className="h-5 w-5" style={{ color: "#3B6FD4" }} />}
                  label="Contractors"
                  value={data.totals.contractors}
                  color="#3B6FD4"
                />
                <StatCard
                  icon={<Briefcase className="h-5 w-5" style={{ color: "#7C3AED" }} />}
                  label="Total Jobs"
                  value={data.totals.jobs}
                  color="#7C3AED"
                />
                <StatCard
                  icon={<Briefcase className="h-5 w-5" style={{ color: "#16A34A" }} />}
                  label="Active Jobs"
                  value={data.totals.activeJobs}
                  color="#16A34A"
                />
                <StatCard
                  icon={<Users className="h-5 w-5" style={{ color: "#0891B2" }} />}
                  label="Matches"
                  value={data.totals.matches}
                  color="#0891B2"
                />
                <StatCard
                  icon={<Handshake className="h-5 w-5" style={{ color: "#16A34A" }} />}
                  label="Hires"
                  value={data.totals.hires}
                  color="#16A34A"
                />
                <StatCard
                  icon={<Star className="h-5 w-5" style={{ color: "#EAB308" }} />}
                  label="Ratings"
                  value={data.totals.ratings}
                  color="#EAB308"
                />
                <StatCard
                  icon={<TrendingUp className="h-5 w-5" style={{ color: "#E06B20" }} />}
                  label="Hire Rate"
                  value={data.funnel.hireRate}
                  color="#E06B20"
                />
              </div>
            </div>

            {/* 7-day signups */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Last 7 Days
              </h2>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-lg font-bold">
                      {data.funnel.signups7d.total} New Signups
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center rounded-lg bg-orange-50 p-3">
                      <p className="text-2xl font-bold text-orange-600">
                        {data.funnel.signups7d.workers}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Workers
                      </p>
                    </div>
                    <div className="text-center rounded-lg bg-blue-50 p-3">
                      <p className="text-2xl font-bold text-blue-600">
                        {data.funnel.signups7d.contractors}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Contractors
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Conversion funnel */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Conversion Funnel
              </h2>
              <Card>
                <CardContent className="p-5 space-y-3">
                  <FunnelBar
                    label="Jobs Posted"
                    value={data.totals.jobs}
                    max={data.totals.jobs}
                    color="#7C3AED"
                  />
                  <FunnelBar
                    label="Matches Generated"
                    value={data.totals.matches}
                    max={data.totals.jobs}
                    color="#0891B2"
                  />
                  <FunnelBar
                    label="Successful Hires"
                    value={data.totals.hires}
                    max={data.totals.jobs}
                    color="#16A34A"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
      <BottomNav userType="contractor" />
    </div>
  );
}

function FunnelBar({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const percent = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold">{value}</span>
      </div>
      <div className="h-3 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percent}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
